/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Alert, AlertColor, Snackbar } from "@mui/material";
import { EnvironmentState, IEnvironment } from "../../../types/EnvironmentTypes";
import { isScalableEnvironment, useEnvironmentService } from "../../../services/registry/EnvironmentService";
import { EnvironmentCard } from "./EnvironmentCard";
import { EnvironmentForm, EnvironmentFormData, EnvironmentFormType } from "./EnvironmentForm";
import { YesNoCancelPopup } from "../../../components/YesNoCancelPopup";
import { IBackendImage, useBackendImageService } from "../../../services/registry/BackendImageService";
import { DeploymentPopup } from "./DeploymentPopup";
import { AppContext } from "utils/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import Routes from "../../../components/routing/Routes";
import { useIsAuthenticated } from "../../../services/auth/AuthService";
import { LogPopup } from "../../../components/LogPopup";
import { PackagePickerDialog } from "components/PackagePickerDialog";
import { IPackage } from "services/registry/PackageService";
import { SchemaBranchPickerDialog } from "components/SchemaBranchPickerDialog";
import { IntegerInputPopup } from "components/IntegerInputPopup";
import { ServerImageBranchPickerDialog } from "components/ServerImageBranchPickerDialog";
import { ServerImagePickerDialog } from "components/ServerImagePickerDialog";
import { IServerImage, useServerImageService } from "services/registry/ServerImageService";
import { useSystemService } from "services/backend/SystemService";
import { TableTabPanel } from "views/Registry/Environments/TableTabPanel";
import { PipelineTabPanel } from "views/Registry/Environments/PipelineTabPanel";
import { tabProps, TabPanelContainer } from "components/TabPanelContainer";
import { EnvironmentReplicationPopup, ReplicationItem } from "views/Registry/Environments/EnvironmentReplicationPopup";
import { EnvironmentFeaturesPopup } from "views/Registry/Environments/EnvironmentFeaturesPopup";
import { EnvironmentVariablesOverridesPopup } from "views/Registry/Environments/EnvironmentVariablesOverridesPopup";
import { InstanceTypePickerDialog } from "../../../components/InstanceTypePickerDialog";
import { InstanceType } from "../../../components/InstanceTypePicker";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { StringInputPopup } from "../../../components/StringInputPopup";
import { useObservabilityService } from "../../../services/backend/ObservabilityService";
import { EnvironmentMetricsPopup } from "./EnvironmentMetricsPopup";
import { EnvironmentSchedulerPopup } from "./EnvironmentSchedulerPopup";
import { DockerStatusPopup } from "./DockerStatusPopup";

interface IEnvironmentExtraData {
  isDefault: boolean | undefined;
  state: EnvironmentState;
  publicIpAddress: string | undefined;
  privateIpAddress: string | undefined;
  gameServerCount: number;
  activePackageId: string | undefined;
  activeSchemaBranch: string | undefined;
  activeSchemaVersion: number | undefined;
  nextRefreshTime: number;
  serverImageBranch: string | undefined;
  serverImageId: string | undefined;
}
const envExtraData = new Map<string, IEnvironmentExtraData>();
const unknownEnvExtraData: IEnvironmentExtraData = {
  isDefault: undefined,
  state: EnvironmentState.UNKNOWN,
  publicIpAddress: undefined,
  privateIpAddress: undefined,
  gameServerCount: 0,
  activePackageId: undefined,
  activeSchemaBranch: undefined,
  activeSchemaVersion: undefined,
  nextRefreshTime: 0,
  serverImageBranch: undefined,
  serverImageId: undefined,
};
const ENV_EXTRA_DATA_REFRESH_SLOW_INTERVAL = 90000;
const ENV_EXTRA_DATA_REFRESH_REGULAR_INTERVAL = 30000;
const ENV_EXTRA_DATA_REFRESH_FAST_INTERVAL = 5000;
const ENV_EXTRA_DATA_LOOP_REFRESH_INTERVAL = 1000;
const STORAGE_ENV_FILTER_KEY = "envFilterQuery";

const Environments = () => {
  const navigate = useNavigate();

  const environmentService = useEnvironmentService();
  const backendImageService = useBackendImageService();
  const serverImageService = useServerImageService();
  const systemService = useSystemService();
  const observabilityService = useObservabilityService();
  const isAuthenticated = useIsAuthenticated();

  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [rows, setRows] = useState<IEnvironment[]>([]);
  const [selectedItem, setSelectedItem] = useState<IEnvironment | undefined>(undefined);
  const selectedItemRef = React.useRef<IEnvironment | undefined>(undefined);
  const [linkedItem, setLinkedItem] = useState<IEnvironment | undefined>(undefined);
  const [environmentFormVisible, setEnvironmentFormVisible] = useState<boolean>(false);
  const [environmentFormType, setEnvironmentFormType] = useState<EnvironmentFormType>("new");
  const [terminatePopupVisible, setTerminatePopupVisible] = useState<boolean>(false);
  const [destroyDevPopupVisible, setDestroyDevPopupVisible] = useState<boolean>(false);
  const [revivePopupVisible, setRevivePopupVisible] = useState<boolean>(false);
  const [defaultPopupVisible, setDefaultPopupVisible] = useState<boolean>(false);
  const [stateRefreshTick, setStateRefreshTick] = useState<number>(0);
  const [visibleRows, setVisibleRows] = useState<IEnvironment[]>([]);
  const [deploymentPopupVisible, setDeploymentPopupVisible] = useState<boolean>(false);
  const [selectedDeploymentLog, setSelectedDeploymentLog] = useState<string | undefined>(undefined);
  const [initialFilterQuery, setInitialFilterQuery] = useState<string>("");
  const [activePackagePopupVisible, setActivePackagePopupVisible] = useState<boolean>(false);
  const [activeSchemaBranchPopupVisible, setActiveSchemaBranchPopupVisible] = useState<boolean>(false);
  const [activeSchemaVersionPopupVisible, setActiveSchemaVersionPopupVisible] = useState<boolean>(false);
  const [serverImageBranchPopupVisible, setServerImageBranchPopupVisible] = useState<boolean>(false);
  const [serverImageIdPopupVisible, setServerImageIdPopupVisible] = useState<boolean>(false);
  const [resetPopupVisible, setResetPopupVisible] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);
  const [replicationPopupVisible, setReplicationPopupVisible] = useState<boolean>(false);
  const [featuresPopupVisible, setFeaturesPopupVisible] = useState<boolean>(false);
  const [variablesOverridesPopupVisible, setVariablesOverridesPopupVisible] = useState<boolean>(false);
  const [schedulerPopupVisible, setSchedulerPopupVisible] = useState<boolean>(false);
  const [backendImages, setBackendImages] = useState<IBackendImage[]>([]);
  const [serverImages, setServerImages] = useState<IServerImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [draftStaticDataConfirmVisible, setDraftStaticDataConfirmVisible] = useState<boolean>(false);
  const [pendingReplicationData, setPendingReplicationData] = useState<
    | {
        sourceEnvironment: IEnvironment;
        destEnvironment: IEnvironment;
        itemsToReplicate: ReplicationItem[];
      }
    | undefined
  >(undefined);
  const [instanceTypePickerVisible, setInstanceTypePickerVisible] = useState<boolean>(false);
  const [restartInstancePopupVisible, setRestartInstancePopupVisible] = useState<boolean>(false);
  const [cloudWatchDashboardPopupVisible, setCloudWatchDashboardPopupVisible] = useState<boolean>(false);
  const filterInitRef = useRef(false);
  const [metricsPopups, setMetricsPopups] = useState<Map<string, number>>(new Map());
  const [nextZIndex, setNextZIndex] = useState(2000);
  const [cleanupDiskPopupVisible, setCleanupDiskPopupVisible] = useState<boolean>(false);
  const [cleanupDiskEnvironmentName, setCleanupDiskEnvironmentName] = useState<string | undefined>(undefined);
  const [softRestartPopupVisible, setSoftRestartPopupVisible] = useState<boolean>(false);
  const [dockerStatusPopupVisible, setDockerStatusPopupVisible] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (!filterInitRef.current && rows && rows.length > 0) {
      const name = searchParams.get("name");
      if (name) {
        const item = rows.find((t) => t.name == name);
        if (item) {
          setSelectedItem(item);
          setInitialFilterQuery(name);
        }
      } else {
        const savedFilter = localStorage.getItem(STORAGE_ENV_FILTER_KEY);
        setInitialFilterQuery((savedFilter && savedFilter.trim()) || "dev1");
      }
      filterInitRef.current = true;
    }
  }, [searchParams, rows]);

  const fetchBackendImages = async () => {
    try {
      const { list } = await backendImageService.listBackendImages();
      setBackendImages(list as IBackendImage[]);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of backend images.");
      setAlertSeverity("error");
    }
  };

  const fetchServerImages = async () => {
    try {
      const { list } = await serverImageService.listServerImages();
      setServerImages(list as IServerImage[]);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of server images.");
      setAlertSeverity("error");
    }
  };

  useEffect(() => {
    (async () => fetchBackendImages())();
    (async () => fetchServerImages())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedItem) {
      selectedItemRef.current = selectedItem;
    }
  }, [selectedItem]);

  useEffect(() => {
    if (tabValue === 1) {
      refreshButtonClickHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const replaceRows = (list: IEnvironment[]) => {
    for (const env of list) {
      // Populating with known extra data.
      const extraData = envExtraData.get(env.name);
      if (extraData) {
        env.isDefault = extraData.isDefault;
        env.state = extraData.state;
        env.publicIpAddress = extraData.publicIpAddress;
        env.privateIpAddress = extraData.privateIpAddress;
        env.gameServerCount = extraData.gameServerCount;
        env.activePackageId = extraData.activePackageId;
        env.activeSchemaBranch = extraData.activeSchemaBranch;
        env.activeSchemaVersion = extraData.activeSchemaVersion;
        env.serverImageBranch = extraData.serverImageBranch;
        env.serverImageId = extraData.serverImageId;
      }
    }
    setRows(list);
    if (selectedItemRef.current) {
      // Refreshing the selected item if some data changed.
      let match = false;
      for (const env of list) {
        if (env.name === selectedItemRef.current.name && env.state !== EnvironmentState.TERMINATED) {
          setSelectedItem(env);
          match = true;
        }
      }
      if (!match) {
        // Or unselecting if the selected item does not exist anymore.
        setSelectedItem(undefined);
      }
    }
  };

  const fetchRows = async () => {
    try {
      const { list } = await environmentService.listEnvironments();
      for (const env of list) {
        // Keeping track of the isDefault property
        const extraData = envExtraData.get(env.name) || { ...unknownEnvExtraData };
        extraData.isDefault = env.isDefault;
        envExtraData.set(env.name, extraData);
      }
      replaceRows(list as IEnvironment[]);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of environments.");
      setAlertSeverity("error");
    }
  };

  useEffect(() => {
    let stateRefreshTick = 0;
    const stateRefreshIntervalId = setInterval(() => {
      setStateRefreshTick(++stateRefreshTick);
    }, ENV_EXTRA_DATA_LOOP_REFRESH_INTERVAL);
    return () => {
      clearInterval(stateRefreshIntervalId);
    };
  }, []);

  const refreshRowState = (envName: string) => {
    if (replicationPopupVisible) {
      return;
    }

    const extraData = envExtraData.get(envName) || { ...unknownEnvExtraData };
    extraData.state = EnvironmentState.REFRESHING;
    extraData.nextRefreshTime = Date.now() + ENV_EXTRA_DATA_REFRESH_FAST_INTERVAL;
    envExtraData.set(envName, extraData);
    replaceRows(Array.from(rows));
  };

  const unknownRowState = (envName: string) => {
    const extraData = envExtraData.get(envName) || { ...unknownEnvExtraData };
    extraData.state = EnvironmentState.UNKNOWN;
    extraData.nextRefreshTime = Date.now() + ENV_EXTRA_DATA_REFRESH_REGULAR_INTERVAL;
    envExtraData.set(envName, extraData);
    replaceRows(Array.from(rows));
  };

  const updateRowWithState = (env: IEnvironment) => {
    let interval = ENV_EXTRA_DATA_REFRESH_SLOW_INTERVAL;
    if (
      env.state === EnvironmentState.PENDING_INITIALIZATION ||
      env.state === EnvironmentState.PENDING_TERMINATION ||
      env.state === EnvironmentState.INITIALIZED ||
      env.state === EnvironmentState.REFRESHING
    ) {
      interval = ENV_EXTRA_DATA_REFRESH_FAST_INTERVAL;
    } else if (
      env.state === EnvironmentState.UNHEALTHY ||
      env.state === EnvironmentState.RUNNING ||
      env.name === selectedItem?.name
    ) {
      interval = ENV_EXTRA_DATA_REFRESH_REGULAR_INTERVAL;
    }
    const extraData = envExtraData.get(env.name) || { ...unknownEnvExtraData };
    extraData.state = env.state || EnvironmentState.UNKNOWN;
    extraData.publicIpAddress = env.publicIpAddress || "N/A";
    extraData.privateIpAddress = env.privateIpAddress || "N/A";
    if (env.isDefault !== undefined) {
      extraData.isDefault = env.isDefault;
    }
    if (env.gameServerCount !== undefined) {
      extraData.gameServerCount = env.gameServerCount;
    }
    if (env.activePackageId !== undefined) {
      extraData.activePackageId = env.activePackageId;
    }
    if (env.activeSchemaBranch !== undefined) {
      extraData.activeSchemaBranch = env.activeSchemaBranch;
    }
    if (env.activeSchemaVersion !== undefined) {
      extraData.activeSchemaVersion = env.activeSchemaVersion;
    }
    if (env.serverImageBranch !== undefined) {
      extraData.serverImageBranch = env.serverImageBranch;
    }
    if (env.serverImageId !== undefined) {
      extraData.serverImageId = env.serverImageId;
    }
    extraData.nextRefreshTime = Date.now() + interval;
    envExtraData.set(env.name, extraData);
    const updatedRows = Array.from(rows);
    for (const i in updatedRows) {
      if (updatedRows[i].name === env.name) {
        updatedRows[i] = env;
      }
    }
    replaceRows(updatedRows);
  };

  const evalEnvStateState = async (envName: string) => {
    refreshRowState(envName);
    try {
      const envWithState = await environmentService.evalEnvironmentState(envName);
      updateRowWithState(envWithState);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Could not eval the state of the environment "${envName}".`);
      setAlertSeverity("warning");
      unknownRowState(envName);
    }
  };

  useEffect(() => {
    if (stateRefreshTick === 0) {
      fetchRows();
      setStateRefreshTick(1);
    } else {
      const time = Date.now();
      if (selectedItem) {
        const extraData = envExtraData.get(selectedItem.name) || unknownEnvExtraData;
        if (time > extraData.nextRefreshTime || extraData.publicIpAddress === undefined) {
          evalEnvStateState(selectedItem.name);
          return;
        }
      }
      if (tabValue === 0) {
        const rowsToRefresh = visibleRows.filter(
          (t) => time > (envExtraData.get(t.name) || unknownEnvExtraData).nextRefreshTime,
        );
        if (rowsToRefresh.length > 0) {
          evalEnvStateState(rowsToRefresh[0].name);
        }
      } else {
        const rowsToRefresh = rows.filter(
          (t) => t.terminationDate === undefined && time > (envExtraData.get(t.name) || unknownEnvExtraData).nextRefreshTime,
        );
        for (const t of rowsToRefresh) {
          evalEnvStateState(t.name);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateRefreshTick, visibleRows, tabValue]);

  const newButtonClickHandler = () => {
    setEnvironmentFormVisible(true);
    setEnvironmentFormType("new");
  };

  const pageChangeHandler = (page: number, rowsPerPage: number, visibleRows: IEnvironment[]) => {
    setVisibleRows(visibleRows);
  };

  const editButtonClickHandler = () => {
    setEnvironmentFormVisible(true);
    setEnvironmentFormType("edit");
  };

  const environmentFormDismissHandler = () => {
    setEnvironmentFormVisible(false);
  };

  const environmentFormAcceptHandler = async (data: EnvironmentFormData) => {
    setEnvironmentFormVisible(false);
    if (environmentFormType === "new") {
      if (data.useTerraform) {
        try {
          await environmentService.createDevEnvironment(data.name, data.stage, data.ttl, data.clientBranch);
        } catch (e: any) {
          setAlertMessage(e.debugMessage || "Could not create the new environment.");
          setAlertSeverity("error");
        }
      } else {
        try {
          await environmentService.addEnvironment(data.name, data.baseURL, data.stage, data.ttl, data.clientBranch);
        } catch (e: any) {
          setAlertMessage(e.debugMessage || "Could not add the new environment.");
          setAlertSeverity("error");
        }
      }
    } else {
      try {
        await environmentService.editEnvironment(
          data.name,
          data.baseURL,
          data.stage,
          data.ttl,
          data.clientBranch,
          data.ownerEmail,
        );
      } catch (e: any) {
        setAlertMessage(e.debugMessage || "Could not edit the selected environment.");
        setAlertSeverity("error");
      }
    }
    await fetchRows();
    const extraData = envExtraData.get(data.name) || { ...unknownEnvExtraData };
    extraData.state = EnvironmentState.REFRESHING;
    extraData.nextRefreshTime = Date.now() + ENV_EXTRA_DATA_REFRESH_FAST_INTERVAL;
    envExtraData.set(data.name, extraData);
  };

  const terminateButtonClickHandler = () => {
    setTerminatePopupVisible(true);
  };

  const terminatePopupNoHandler = () => {
    setTerminatePopupVisible(false);
  };

  const terminatePopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }
    setTerminatePopupVisible(false);
    if (selectedItem.stage <= 7 && selectedItem.state !== EnvironmentState.UNKNOWN) {
      setDestroyDevPopupVisible(true);
    } else {
      try {
        await environmentService.terminateEnvironment(selectedItem.name);
      } catch (e: any) {
        setAlertMessage(e.debugMessage || "Could not terminate the selected environment.");
        setAlertSeverity("error");
      }
    }
    refreshRowState(selectedItem.name);
  };

  const destroyDevPopupNoHandler = () => {
    setDestroyDevPopupVisible(false);
  };

  const destroyDevPopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }
    setDestroyDevPopupVisible(false);
    try {
      await environmentService.destroyDevEnvironment(selectedItem.name);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not terminate the selected environment.");
      setAlertSeverity("error");
    }
    refreshRowState(selectedItem.name);
  };

  const reviveButtonClickHandler = () => {
    setRevivePopupVisible(true);
  };

  const revivePopupNoHandler = () => {
    setRevivePopupVisible(false);
  };

  const revivePopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      await environmentService.createDevEnvironment(selectedItem.name, selectedItem.stage);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not revive the selected environment.");
      setAlertSeverity("error");
    }
    setRevivePopupVisible(false);
    refreshRowState(selectedItem.name);
  };

  const defaultButtonClickHandler = () => {
    setDefaultPopupVisible(true);
  };

  const defaultPopupNoHandler = () => {
    setDefaultPopupVisible(false);
  };

  const defaultPopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }
    setDefaultPopupVisible(false);
    try {
      await environmentService.setDefaultEnvironment(selectedItem.name);
      fetchRows();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not set the selected environment as default.");
      setAlertSeverity("error");
    }
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const refreshButtonClickHandler = () => {
    if (replicationPopupVisible) {
      return;
    }

    for (const t of Array.from(envExtraData.values())) {
      t.state = EnvironmentState.REFRESHING;
      t.nextRefreshTime = 0;
    }
  };

  const markEnvStateAsDeploying = (envName: string) => {
    const extraData = envExtraData.get(envName) || { ...unknownEnvExtraData };
    extraData.state = EnvironmentState.DEPLOYING;
    extraData.nextRefreshTime = Date.now() + ENV_EXTRA_DATA_REFRESH_REGULAR_INTERVAL;
    envExtraData.set(envName, extraData);
    replaceRows(Array.from(rows));
  };

  const deployBackendImageButtonClickHandler = () => {
    setDeploymentPopupVisible(true);
  };

  const deployButtonClickHandler = async (backendImage: IBackendImage, backendFlavor: string) => {
    if (!selectedItem) {
      return;
    }
    try {
      setDeploymentPopupVisible(false);
      if (selectedItem.name === "registry") {
        await environmentService.deployToDevRegistry(backendImage.id, backendFlavor);
      } else {
        await environmentService.deployToDevEnvironment(selectedItem.name, backendImage.id, backendFlavor);
      }
      markEnvStateAsDeploying(selectedItem.name);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not deploy to the selected environment.");
      setAlertSeverity("error");
    }
  };

  const changeActivePackageButtonClickHandler = async (pkg: IPackage) => {
    if (!selectedItem) {
      setActivePackagePopupVisible(false);
      return;
    }
    try {
      await environmentService.overrideFeature(selectedItem.name, "active-package-id", pkg.id);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not set the active package.");
      setAlertSeverity("error");
    }
    setActivePackagePopupVisible(false);
    refreshButtonClickHandler();
  };

  const viewActivePackageButtonClickHandler = () => {
    window.open("https://app.growthbook.io/features/active-package-id");
  };

  const changeActiveSchemaBranchButtonClickHandler = async (branch: string) => {
    if (!selectedItem) {
      setActiveSchemaBranchPopupVisible(false);
      return;
    }
    try {
      await environmentService.overrideFeature(selectedItem.name, "active-schema-branch", branch);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not set the active schema branch.");
      setAlertSeverity("error");
    }
    setActiveSchemaBranchPopupVisible(false);
    refreshButtonClickHandler();
  };

  const viewActiveSchemaBranchButtonClickHandler = () => {
    window.open("https://app.growthbook.io/features/active-schema-branch");
  };

  const changeActiveSchemaVersionButtonClickHandler = async (version: number) => {
    if (!selectedItem) {
      setActiveSchemaVersionPopupVisible(false);
      return;
    }
    try {
      await environmentService.overrideFeature(selectedItem.name, "active-schema-version", version);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not set the active schema version.");
      setAlertSeverity("error");
    }
    setActiveSchemaVersionPopupVisible(false);
    refreshButtonClickHandler();
  };

  const viewActiveSchemaVersionButtonClickHandler = () => {
    window.open("https://app.growthbook.io/features/active-schema-version");
  };

  const changeServerImageBranchButtonClickHandler = async (branch: string) => {
    if (!selectedItem) {
      setServerImageBranchPopupVisible(false);
      return;
    }
    try {
      await environmentService.overrideFeature(selectedItem.name, "server-image-branch", branch);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not set the server image branch.");
      setAlertSeverity("error");
    }
    setServerImageBranchPopupVisible(false);
    refreshButtonClickHandler();
  };

  const viewServerImageBranchButtonClickHandler = () => {
    window.open("https://app.growthbook.io/features/server-image-branch");
  };

  const changeServerImageIdButtonClickHandler = async (serverImage: IServerImage | null) => {
    if (!selectedItem) {
      setServerImageIdPopupVisible(false);
      return;
    }
    try {
      await environmentService.overrideFeature(selectedItem.name, "server-image-id", serverImage?.id || "");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not set the server image id.");
      setAlertSeverity("error");
    }
    setServerImageIdPopupVisible(false);
    refreshButtonClickHandler();
  };

  const viewServerImageIdButtonClickHandler = () => {
    window.open("https://app.growthbook.io/features/server-image-id");
  };

  const context = React.useContext(AppContext);
  useEffect(() => {
    context?.setTitle("Environments");
    context?.setTargetAPI("registry");
  }, [context]);

  const showLogPopupClickHandler = (logName: string) => {
    setSelectedDeploymentLog(logName);
  };

  const showGameServersButtonClickHandler = () => {
    context?.setSelectedEnvironment(selectedItem);
    navigate(Routes.gameServers);
  };

  const showLogViewClickHandler = (logName: string) => {
    window.open(`${window.location.origin}/logging/?name=${logName}`);
  };

  const showAllLogsButtonClickHandler = () => {
    if (selectedItem) {
      window.open(`${window.location.origin}/logging/?name=${selectedItem.name}`);
    }
  };

  const closeLogPopupClickHandler = () => {
    setSelectedDeploymentLog(undefined);
  };

  const resetButtonClickHandler = () => {
    setResetPopupVisible(true);
  };

  const resetPopupNoHandler = () => {
    setResetPopupVisible(false);
  };

  const clearSDCacheButtonClickHandler = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      setLoading(true);
      await systemService.clearStaticDataCache(selectedItem.baseURL);
      setAlertMessage("SD cache succssfully cleared.");
      setAlertSeverity("success");
    } catch (error: any) {
      setAlertMessage(error.debugMessage || "Could not clear the SD cache.");
    } finally {
      setLoading(false);
    }
  };

  const resetPopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }
    setResetPopupVisible(false);
    try {
      setLoading(true);
      await systemService.resetEnvironment(selectedItem.baseURL);
      setAlertMessage("Environment successfully reset.");
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not reset the environment.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
    setTimeout(refreshButtonClickHandler, 500);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const replicateFromButtonClickHandler = () => {
    setReplicationPopupVisible(true);
  };

  const replicationPopupDismissHandler = () => {
    setReplicationPopupVisible(false);
  };

  const replicationPopupReplicateHandler = async (
    sourceEnvironment: IEnvironment,
    destEnvironment: IEnvironment,
    itemsToReplicate: ReplicationItem[],
  ) => {
    await performReplication(sourceEnvironment, destEnvironment, itemsToReplicate, false);
  };

  const editFeaturesButtonClickHandler = () => {
    setFeaturesPopupVisible(true);
  };

  const overrideEnvironmentVariablesButtonClickHandler = () => {
    setVariablesOverridesPopupVisible(true);
  };

  const variablesOverridesPopupDismissHandler = () => {
    setVariablesOverridesPopupVisible(false);
  };

  const variablesOverridesPopupSaveHandler = async (environment: IEnvironment, overrides: Record<string, string>) => {
    try {
      await environmentService.overrideFeature(environment.name, "devenv-variables-overrides", overrides);
      setAlertMessage(`Successfully updated environment variable overrides for environment ${environment.name}`);
      setAlertSeverity("success");
    } catch (error) {
      console.error("Failed to save environment variable overrides:", error);
      setAlertMessage(`Failed to save environment variable overrides: ${error}`);
      setAlertSeverity("error");
    } finally {
      refreshButtonClickHandler();
    }
  };

  const schedulerButtonClickHandler = () => {
    setSchedulerPopupVisible(true);
  };

  const schedulerPopupDismissHandler = () => {
    setSchedulerPopupVisible(false);
  };

  const changeScheduleButtonClickHandler = async (environmentName: string, conf: string) => {
    if (!selectedItem) {
      setSchedulerPopupVisible(false);
      return;
    }
    try {
      setSchedulerPopupVisible(false);
      setLoading(true);
      await environmentService.overrideFeature(environmentName, "env-auto-stop-conf", conf);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not set the schedule.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
      setSchedulerPopupVisible(false);
      refreshButtonClickHandler();
    }
  };

  const featuresPopupDismissHandler = () => {
    setFeaturesPopupVisible(false);
  };

  const featuresPopupSaveHandler = async (environment: IEnvironment, changedFeatures: any[]) => {
    try {
      for (const feature of changedFeatures) {
        await environmentService.overrideFeature(environment.name, feature.key, feature.currentValue);
      }
      setAlertMessage(`Successfully updated ${changedFeatures.length} feature(s) for environment ${environment.name}`);
      setAlertSeverity("success");
    } catch (error) {
      console.error("Failed to save features:", error);
      setAlertMessage(`Failed to save features: ${error}`);
      setAlertSeverity("error");
    } finally {
      refreshButtonClickHandler();
    }
  };

  const performReplication = async (
    sourceEnvironment: IEnvironment,
    destEnvironment: IEnvironment,
    itemsToReplicate: ReplicationItem[],
    autoPublishDraftStaticData: boolean,
  ) => {
    if (!selectedItem) {
      return;
    }

    try {
      setLoading(true);

      // Extract features to replicate - map special cases
      const features = itemsToReplicate
        .filter((item) => item.replicate)
        .map((item) => {
          // Map backendImageId to the expected feature name
          if (item.key === "backendImageId") {
            return "backend-image-id";
          }
          return item.key;
        });

      // Determine if we should skip deployment based on whether backendImageId is being replicated
      const backendImageIdItem = itemsToReplicate.find((item) => item.key === "backendImageId");
      const skipDeployment = !backendImageIdItem?.replicate;

      setReplicationPopupVisible(false);

      if (destEnvironment.name === "registry") {
        await environmentService.replicateToDevRegistry(
          sourceEnvironment.name,
          destEnvironment.name,
          features,
          autoPublishDraftStaticData,
          skipDeployment,
        );
      } else {
        await environmentService.replicateToDevEnvironment(
          sourceEnvironment.name,
          destEnvironment.name,
          features,
          autoPublishDraftStaticData,
          skipDeployment,
        );
      }

      if (!skipDeployment) {
        markEnvStateAsDeploying(destEnvironment.name);
      }

      setAlertMessage("Environment replication started successfully.");
      setAlertSeverity("success");
    } catch (e: any) {
      console.log("Replication error:", e);
      console.log("Replication error 2:", e.errorCode, 0x1006000a, autoPublishDraftStaticData);
      // Check if it's a DraftStaticDataNotAllowed error (errorCode === 0x1006000A)
      if (e.errorCode === 0x1006000a && !autoPublishDraftStaticData) {
        // Store the replication data for retry and show confirmation popup
        setPendingReplicationData({ sourceEnvironment, destEnvironment, itemsToReplicate });
        setDraftStaticDataConfirmVisible(true);
      } else {
        setAlertMessage(e.debugMessage || "Could not replicate the environment.");
        setAlertSeverity("error");
      }
    } finally {
      setLoading(false);
    }
  };

  const draftStaticDataConfirmYesHandler = async () => {
    setDraftStaticDataConfirmVisible(false);
    if (pendingReplicationData) {
      await performReplication(
        pendingReplicationData.sourceEnvironment,
        pendingReplicationData.destEnvironment,
        pendingReplicationData.itemsToReplicate,
        true,
      );
      setPendingReplicationData(undefined);
    }
  };

  const draftStaticDataConfirmNoHandler = () => {
    setDraftStaticDataConfirmVisible(false);
    setPendingReplicationData(undefined);
  };

  const handleLinkClick = (srcEnvironment: IEnvironment, dstEnvironment: IEnvironment) => {
    setSelectedItem(dstEnvironment);
    setLinkedItem(srcEnvironment);
    setReplicationPopupVisible(true);
  };

  const changeInstanceTypeButtonClickHandler = () => {
    setInstanceTypePickerVisible(true);
  };

  const restartInstanceButtonClickHandler = () => {
    setRestartInstancePopupVisible(true);
  };

  const startStopInstanceButtonClickHandler = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      setLoading(true);
      if (selectedItem.state === EnvironmentState.STOPPED) {
        await environmentService.startEnvironment(selectedItem.name);
      } else if (selectedItem.state === EnvironmentState.HEALTHY || selectedItem.state === EnvironmentState.RUNNING) {
        await environmentService.stopEnvironment(selectedItem.name);
      } else {
        setAlertMessage("The environment must be in RUNNING or STOPPED state to perform this operation.");
        setAlertSeverity("info");
        return;
      }
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not start/stop the environment.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
    refreshRowState(selectedItem.name);
  };

  const restartInstancePopupNoHandler = () => {
    setRestartInstancePopupVisible(false);
  };

  const restartInstancePopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }
    setRestartInstancePopupVisible(false);
    try {
      setLoading(true);
      await environmentService.restartEnvironment(selectedItem.name);
      markEnvStateAsDeploying(selectedItem.name);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not restart the environment.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const instanceTypePickerCancelHandler = () => {
    setInstanceTypePickerVisible(false);
  };

  const instanceTypePickerActionHandler = async (instanceType: InstanceType) => {
    if (!selectedItem) {
      setInstanceTypePickerVisible(false);
      return;
    }

    const currentInstanceType = selectedItem.instanceType || "t4g.small";
    if (instanceType.value === currentInstanceType) {
      setInstanceTypePickerVisible(false);
      setAlertMessage("The selected instance type is the same as the current one.");
      setAlertSeverity("info");
      return;
    }

    try {
      await environmentService.upgradeEnvironment(selectedItem.name, instanceType.value);
      setAlertMessage(
        `Successfully started upgrading environment "${selectedItem.name}" to instance type "${instanceType.value}".`,
      );
      setAlertSeverity("success");
      markEnvStateAsDeploying(selectedItem.name);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not upgrade the environment instance type.");
      setAlertSeverity("error");
    }
    setInstanceTypePickerVisible(false);
  };

  const viewCloudWatchDashboardsButtonClickHandler = () => {
    window.open("https://335100553624.us-east-1.console.aws.amazon.com/cloudwatch/home?region=us-east-1#dashboards/");
  };

  const createCloudWatchDashboardButtonClickHandler = () => {
    setCloudWatchDashboardPopupVisible(true);
  };

  const createCloudWatchDashboardPopupDismissHandler = () => {
    setCloudWatchDashboardPopupVisible(false);
  };

  const createCloudWatchDashboardPopupAcceptHandler = async (dashboardName: string) => {
    if (!selectedItem) {
      setCloudWatchDashboardPopupVisible(false);
      return;
    }
    setCloudWatchDashboardPopupVisible(false);
    try {
      setLoading(true);
      await observabilityService.createOrUpdateCwDashboard(selectedItem?.baseURL, dashboardName);
      setAlertMessage(`Successfully created/updated CloudWatch dashboard: ${dashboardName}`);
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not create/update CloudWatch dashboard.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const showMetricsButtonClickHandler = () => {
    if (!selectedItem) {
      return;
    }
    const newMetricsPopups = new Map(metricsPopups);
    newMetricsPopups.set(selectedItem.name, nextZIndex);
    setMetricsPopups(newMetricsPopups);
    setNextZIndex(nextZIndex + 1);
  };

  const showDockerStatusButtonClickHandler = () => {
    if (!selectedItem) {
      return;
    }

    if (isScalableEnvironment(selectedItem.stage)) {
      setAlertMessage("Docker events are not available for scalable environments.");
      setAlertSeverity("info");
      return;
    }

    if (
      selectedItem.state === EnvironmentState.STOPPED ||
      selectedItem.state === EnvironmentState.TERMINATED ||
      selectedItem.state === EnvironmentState.REFRESHING
    ) {
      setAlertMessage("Docker events are not available for non-running environments.");
      setAlertSeverity("info");
      return;
    }

    setDockerStatusPopupVisible(true);
  };

  const closeDockerStatusPopupHandler = () => {
    setDockerStatusPopupVisible(false);
  };

  const closeMetricsPopupHandler = (envName: string) => {
    const newMetricsPopups = new Map(metricsPopups);
    newMetricsPopups.delete(envName);
    setMetricsPopups(newMetricsPopups);
  };

  const focusMetricsPopupHandler = (envName: string) => {
    const newMetricsPopups = new Map(metricsPopups);
    newMetricsPopups.set(envName, nextZIndex);
    setMetricsPopups(newMetricsPopups);
    setNextZIndex(nextZIndex + 1);
  };

  const cleanupDiskHandler = (envName: string) => {
    setCleanupDiskEnvironmentName(envName);
    setCleanupDiskPopupVisible(true);
  };

  const cleanupDiskPopupNoHandler = () => {
    setCleanupDiskPopupVisible(false);
    setCleanupDiskEnvironmentName(undefined);
  };

  const cleanupDiskPopupYesHandler = async () => {
    if (!cleanupDiskEnvironmentName) {
      return;
    }
    setCleanupDiskPopupVisible(false);
    try {
      setLoading(true);
      await environmentService.cleanupDiskEnvironment(cleanupDiskEnvironmentName);
      setAlertMessage(`Successfully started disk cleanup for environment "${cleanupDiskEnvironmentName}".`);
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not cleanup disk for the environment.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
      setCleanupDiskEnvironmentName(undefined);
    }
  };

  const softRestartButtonClickHandler = () => {
    setSoftRestartPopupVisible(true);
  };

  const softRestartPopupNoHandler = () => {
    setSoftRestartPopupVisible(false);
  };

  const softRestartPopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }
    setSoftRestartPopupVisible(false);
    try {
      setLoading(true);
      await environmentService.softRestartEnvironment(selectedItem.name);
      setAlertMessage(`Successfully soft-restarted the environment "${selectedItem.name}".`);
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not soft-restart the environment.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Environments" {...tabProps(0)} />
          <Tab label="Pipeline" {...tabProps(1)} />
        </Tabs>
      </Box>
      <TabPanelContainer value={tabValue} index={0}>
        {initialFilterQuery ? (
          <TableTabPanel
            environments={rows}
            backendImages={backendImages}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            onNewButtonClick={newButtonClickHandler}
            onRefreshButtonClick={refreshButtonClickHandler}
            onPageChange={pageChangeHandler}
            initialFilterQuery={initialFilterQuery}
          />
        ) : (
          <LoadingOverlay />
        )}
      </TabPanelContainer>
      <TabPanelContainer value={tabValue} index={1}>
        <PipelineTabPanel
          environments={rows}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          onLinkClick={handleLinkClick}
        />
      </TabPanelContainer>
      {selectedItem && (
        <EnvironmentCard
          content={selectedItem}
          backendImages={backendImages}
          serverImages={serverImages}
          onCloseButtonClick={() => setSelectedItem(undefined)}
          onEditButtonClick={editButtonClickHandler}
          onTerminateButtonClick={terminateButtonClickHandler}
          onReviveButtonClick={reviveButtonClickHandler}
          onSetDefaultButtonClick={defaultButtonClickHandler}
          onDeployBackendImageButtonClick={deployBackendImageButtonClickHandler}
          onChangeActivePackageButtonClick={() => setActivePackagePopupVisible(true)}
          onViewActivePackageButtonClick={viewActivePackageButtonClickHandler}
          onChangeActiveSchemaBranchButtonClick={() => setActiveSchemaBranchPopupVisible(true)}
          onViewActiveSchemaBranchButtonClick={viewActiveSchemaBranchButtonClickHandler}
          onChangeActiveSchemaVersionButtonClick={() => setActiveSchemaVersionPopupVisible(true)}
          onViewActiveSchemaVersionButtonClick={viewActiveSchemaVersionButtonClickHandler}
          onChangeServerImageBranchButtonClick={() => setServerImageBranchPopupVisible(true)}
          onViewServerImageBranchButtonClick={viewServerImageBranchButtonClickHandler}
          onChangeServerImageIdButtonClick={() => setServerImageIdPopupVisible(true)}
          onViewServerImageIdButtonClick={viewServerImageIdButtonClickHandler}
          onShowGameServersButtonClick={showGameServersButtonClickHandler}
          onShowDeploymentLogButtonClick={showLogPopupClickHandler}
          onShowAllLogsButtonClick={showAllLogsButtonClickHandler}
          onResetButtonClick={resetButtonClickHandler}
          onReplicateFromButtonClick={replicateFromButtonClickHandler}
          onChangeInstanceTypeButtonClick={changeInstanceTypeButtonClickHandler}
          onRestartInstanceButtonClick={restartInstanceButtonClickHandler}
          onStartStopInstanceButtonClick={startStopInstanceButtonClickHandler}
          onSoftRestartButtonClick={softRestartButtonClickHandler}
          onEditFeaturesButtonClick={editFeaturesButtonClickHandler}
          onOverrideEnvironmentVariablesButtonClick={overrideEnvironmentVariablesButtonClickHandler}
          onSchedulerButtonClick={schedulerButtonClickHandler}
          onViewCloudWatchDashboardsButtonClick={viewCloudWatchDashboardsButtonClickHandler}
          onCreateCloudWatchDashboardButtonClick={createCloudWatchDashboardButtonClickHandler}
          onShowMetricsButtonClick={showMetricsButtonClickHandler}
          onShowDockerStatusButtonClick={showDockerStatusButtonClickHandler}
          onClearSDCacheButtonClick={clearSDCacheButtonClickHandler}
        />
      )}
      {Array.from(metricsPopups.entries()).map(([envName, zIndex]) => {
        const env = rows.find((e) => e.name === envName);
        if (!env) return null;
        return (
          <EnvironmentMetricsPopup
            key={envName}
            environment={env}
            zIndex={zIndex}
            onClose={() => closeMetricsPopupHandler(envName)}
            onFocus={() => focusMetricsPopupHandler(envName)}
            onCleanupDisk={() => cleanupDiskHandler(envName)}
            fetchMetrics={environmentService.fetchEnvironmentMetrics}
          />
        );
      })}
      {activePackagePopupVisible && (
        <PackagePickerDialog
          visible={activePackagePopupVisible}
          dialogTitle="Select Active Package"
          actionButtonLabel="Select"
          onCancel={() => setActivePackagePopupVisible(false)}
          onAction={changeActivePackageButtonClickHandler}
        />
      )}
      {activeSchemaBranchPopupVisible && (
        <SchemaBranchPickerDialog
          visible={activeSchemaBranchPopupVisible}
          dialogTitle="Select Active Schema Branch (main is the default)"
          actionButtonLabel="Select"
          onCancel={() => setActiveSchemaBranchPopupVisible(false)}
          onAction={changeActiveSchemaBranchButtonClickHandler}
        />
      )}
      {activeSchemaVersionPopupVisible && (
        <IntegerInputPopup
          visible={activeSchemaVersionPopupVisible}
          title="Input Active Schema Version (0 defaults to the latest)"
          onDismiss={() => setActiveSchemaVersionPopupVisible(false)}
          onAccept={changeActiveSchemaVersionButtonClickHandler}
          minValue={0}
        />
      )}
      {selectedDeploymentLog !== undefined && (
        <LogPopup
          visible={true}
          logName={selectedDeploymentLog}
          onClose={closeLogPopupClickHandler}
          onShowLogView={() => showLogViewClickHandler(selectedDeploymentLog)}
        />
      )}
      {serverImageBranchPopupVisible && (
        <ServerImageBranchPickerDialog
          visible={serverImageBranchPopupVisible}
          dialogTitle="Select Server Image Branch"
          actionButtonLabel="Select"
          onCancel={() => setServerImageBranchPopupVisible(false)}
          onAction={changeServerImageBranchButtonClickHandler}
        />
      )}
      {serverImageIdPopupVisible && (
        <ServerImagePickerDialog
          visible={serverImageIdPopupVisible}
          dialogTitle="Select Server Image ID"
          actionButtonLabel="Select"
          allowDefault={true}
          onCancel={() => setServerImageIdPopupVisible(false)}
          onAction={changeServerImageIdButtonClickHandler}
        />
      )}
      <EnvironmentForm
        visible={environmentFormVisible}
        type={environmentFormType}
        initialData={environmentFormType === "edit" ? selectedItem : undefined}
        onDismiss={environmentFormDismissHandler}
        onAccept={environmentFormAcceptHandler}
      />
      <YesNoCancelPopup
        visible={terminatePopupVisible}
        title={"Terminate Environment"}
        message={`Are you sure you want to terminate the Environment named <b>${selectedItem?.name}</b>?<br/>
        If you terminate this Environment, you will permanently block any client from accessing it.`}
        onNo={terminatePopupNoHandler}
        onYes={terminatePopupYesHandler}
      />
      <YesNoCancelPopup
        visible={destroyDevPopupVisible}
        title={"Destroy Environment"}
        message={`Do you want to destroy all AWS resources associated to the Terraform state of this Environment?`}
        onNo={destroyDevPopupNoHandler}
        onYes={destroyDevPopupYesHandler}
      />
      <YesNoCancelPopup
        visible={revivePopupVisible}
        title={"Revive Environment"}
        message={`Do you want to revive this Environment, including all AWS resources associated to it?`}
        onNo={revivePopupNoHandler}
        onYes={revivePopupYesHandler}
      />
      <YesNoCancelPopup
        visible={defaultPopupVisible}
        title={"Set Environment as default"}
        message={`Are you sure you want to set the Environment named <b>${selectedItem?.name}</b> as default?<br/>
        Doing so will redirect all unidentified clients to this Environment.`}
        onNo={defaultPopupNoHandler}
        onYes={defaultPopupYesHandler}
      />
      <YesNoCancelPopup
        visible={restartInstancePopupVisible}
        title={"Restart Instance"}
        message={`Are you sure you want to restart that instance?<br/>
        This operation can take up to 5-10 minutes to complete. Please do not close this tab until the restart is complete.<br/><br/>
        If this operation fails, please contact the backend team immediately.`}
        onNo={restartInstancePopupNoHandler}
        onYes={restartInstancePopupYesHandler}
      />
      {deploymentPopupVisible && (
        <DeploymentPopup
          visible={deploymentPopupVisible}
          onCancel={() => setDeploymentPopupVisible(false)}
          onDeploy={deployButtonClickHandler}
        />
      )}
      <YesNoCancelPopup
        visible={resetPopupVisible}
        title={"Reset Environment"}
        message={`Are you sure you want to reset the Environment named <b>${selectedItem?.name}</b>?<br/>
        This will terminate all game servers and wipe all data. This operation cannot be undone.`}
        onNo={resetPopupNoHandler}
        onYes={resetPopupYesHandler}
      />
      {selectedItem && (
        <EnvironmentReplicationPopup
          visible={replicationPopupVisible}
          initialSourceEnvironment={linkedItem as IEnvironment}
          destEnvironment={selectedItem as IEnvironment}
          onDismiss={replicationPopupDismissHandler}
          onReplicate={replicationPopupReplicateHandler}
        />
      )}
      {selectedItem && (
        <EnvironmentFeaturesPopup
          visible={featuresPopupVisible}
          environment={selectedItem as IEnvironment}
          onDismiss={featuresPopupDismissHandler}
          onSave={featuresPopupSaveHandler}
        />
      )}
      {selectedItem && (
        <EnvironmentVariablesOverridesPopup
          visible={variablesOverridesPopupVisible}
          environment={selectedItem as IEnvironment}
          onDismiss={variablesOverridesPopupDismissHandler}
          onSave={variablesOverridesPopupSaveHandler}
        />
      )}
      {selectedItem && (
        <EnvironmentSchedulerPopup
          visible={schedulerPopupVisible}
          environment={selectedItem as IEnvironment}
          onDismiss={schedulerPopupDismissHandler}
          onSave={changeScheduleButtonClickHandler}
        />
      )}
      <YesNoCancelPopup
        visible={draftStaticDataConfirmVisible}
        title={"Auto-publish Draft Static Data"}
        message={`This replication includes Wads and Packages that are not published yet.<br/>
        Do you want to automatically publish them and proceed with the replication?<br/>
        <b>Wads and Packages cannot be modified once published.</b>`}
        onNo={draftStaticDataConfirmNoHandler}
        onYes={draftStaticDataConfirmYesHandler}
      />
      {instanceTypePickerVisible && (
        <InstanceTypePickerDialog
          visible={instanceTypePickerVisible}
          dialogTitle="Select Instance Type"
          actionButtonLabel="Upgrade"
          dialogMessage={`Changing the instance type of an environment can take up to 5-10 minutes to complete. Please do not close this tab until the upgrade is complete.<br/><br/>
          If this operation fails, please contact the backend team immediately.`}
          onCancel={instanceTypePickerCancelHandler}
          onAction={instanceTypePickerActionHandler}
        />
      )}
      <StringInputPopup
        visible={cloudWatchDashboardPopupVisible}
        title="Create CloudWatch Dashboard"
        message="Only alpha-numeric characters and underscores are allowed in the dashboard name."
        fieldName="Dashboard Name"
        minLength={2}
        maxLength={255}
        onDismiss={createCloudWatchDashboardPopupDismissHandler}
        onAccept={createCloudWatchDashboardPopupAcceptHandler}
      />
      <YesNoCancelPopup
        visible={cleanupDiskPopupVisible}
        title={"Clean-up Disk"}
        message={`Are you sure you want to clean up disk for environment <b>${cleanupDiskEnvironmentName}</b>?<br/>
        This will remove Docker images, build caches, and other temporary files to free up disk space.`}
        onNo={cleanupDiskPopupNoHandler}
        onYes={cleanupDiskPopupYesHandler}
      />
      <YesNoCancelPopup
        visible={softRestartPopupVisible}
        title={"Soft Restart Environment"}
        message={`Are you sure you want to soft-restart the environment <b>${selectedItem?.name}</b>?<br/>
        This will restart the docker containers without stopping the instance.`}
        onNo={softRestartPopupNoHandler}
        onYes={softRestartPopupYesHandler}
      />
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {`${alertMessage} ; isAuthenticated: ${isAuthenticated}`}
        </Alert>
      </Snackbar>
      {dockerStatusPopupVisible && selectedItem && (
        <DockerStatusPopup
          visible={dockerStatusPopupVisible}
          environment={selectedItem}
          onClose={closeDockerStatusPopupHandler}
        />
      )}
      {loading && <LoadingOverlay />}
    </>
  );
};

export default Environments;
